import React, { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../Config";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";

const limpiarRut = (rut) =>
  String(rut || "")
    .replace(/\./g, "")
    .replace(/-/g, "")
    .trim();

const formatearRut = (rut) => {
  const limpio = limpiarRut(rut).toUpperCase();

  if (!limpio) return "";

  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);

  if (!cuerpo) return dv;

  const cuerpoConPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${cuerpoConPuntos}-${dv}`;
};

const calcularDvRut = (cuerpo) => {
  let suma = 0;
  let multiplicador = 2;

  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += Number(cuerpo.charAt(i)) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }

  const resto = 11 - (suma % 11);

  if (resto === 11) return "0";
  if (resto === 10) return "K";
  return String(resto);
};

const validarRutChileno = (rut) => {
  const limpio = limpiarRut(rut).toUpperCase();

  if (!limpio) {
    return { valido: false, mensaje: "El RUT es obligatorio." };
  }

  if (!/^[0-9]+[0-9K]$/.test(limpio)) {
    return {
      valido: false,
      mensaje: "El RUT debe contener solo números y dígito verificador válido.",
    };
  }

  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);

  if (cuerpo.length < 7 || cuerpo.length > 8) {
    return {
      valido: false,
      mensaje:
        "El RUT debe tener entre 7 y 8 dígitos más el dígito verificador.",
    };
  }

  if (/^0+$/.test(cuerpo)) {
    return { valido: false, mensaje: "El RUT no es válido." };
  }

  const dvEsperado = calcularDvRut(cuerpo);

  if (dv !== dvEsperado) {
    return {
      valido: false,
      mensaje: "El dígito verificador del RUT no es válido.",
    };
  }

  return {
    valido: true,
    mensaje: "",
    rutFormateado: formatearRut(limpio),
    rutNormalizado: `${cuerpo}-${dv}`,
  };
};

const Clientes = () => {
  const { baseUrl, urlCobranzas } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [clientes, setClientes] = useState([]);

  const [filtro, setFiltro] = useState("");

  const [dialogVisible, setDialogVisible] = useState(false);
  const [modoDialog, setModoDialog] = useState("crear"); // crear | editar

  const [form, setForm] = useState({
    id: null,
    rut: "",
    razonSocial: "",
    direccion: "",
    comuna: "",
    telefono: "",
    idCobranzas: null,
  });

  const [errores, setErrores] = useState({});
  const [mensaje, setMensaje] = useState(null);

  const joinUrl = (base, path) =>
    `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(
      /^\/+/,
      "",
    )}`;

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const crearClienteEnCobranzas = async ({ id, rut, razonSocial }) => {
    const rutLimpio = limpiarRut(rut).toUpperCase();
    const cobranzasUrl = joinUrl(urlCobranzas, "clientes/agregar");

    const response = await fetch(cobranzasUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id,
        razonSocial,
        rut: rutLimpio,
      }),
      credentials: "include",
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.mensaje || "Error al crear cliente en cobranzas.");
    }

    if (data?.id === null || data?.id === undefined) {
      throw new Error("Cobranzas no devolvió un id de cliente válido.");
    }

    return data.id;
  };

  const limpiarFormulario = () => {
    setForm({
      id: null,
      rut: "",
      razonSocial: "",
      direccion: "",
      comuna: "",
      telefono: "",
      idCobranzas: null,
    });
    setErrores({});
    setMensaje(null);
  };

  const cargarClientes = async () => {
    setIsLoading(true);
    try {
      const data = await fetchJson(joinUrl(baseUrl, "cliente/getClientes"), {
        method: "GET",
      });

      const rows = Array.isArray(data?.data) ? data.data : [];
      setClientes(rows);
    } catch (e) {
      console.error(e);
      alert("Error cargando clientes: " + (e?.message || e));
      setClientes([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    cargarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizar = (valor) =>
    String(valor || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .trim();

  const clientesFiltrados = useMemo(() => {
    const q = normalizar(filtro);
    if (!q) return clientes;

    return (clientes || []).filter((c) => {
      const texto = normalizar(
        [
          c?.rut,
          c?.razonSocial,
          c?.direccion,
          c?.comuna,
          c?.telefono,
          c?.idCobranzas,
        ]
          .filter(Boolean)
          .join(" "),
      );

      return texto.includes(q);
    });
  }, [clientes, filtro]);

  const abrirCrear = () => {
    limpiarFormulario();
    setModoDialog("crear");
    setDialogVisible(true);
  };

  const abrirEditar = (row) => {
    setErrores({});
    setMensaje(null);
    setModoDialog("editar");
    setForm({
      id: row?.id ?? null,
      rut: row?.rut ?? "",
      razonSocial: row?.razonSocial ?? "",
      direccion: row?.direccion ?? "",
      comuna: row?.comuna ?? "",
      telefono: row?.telefono ?? "",
      idCobranzas:
        row?.idCobranzas !== null && row?.idCobranzas !== undefined
          ? Number(row.idCobranzas)
          : null,
    });
    setDialogVisible(true);
  };

  const cerrarDialog = () => {
    setDialogVisible(false);
    limpiarFormulario();
  };

  const setCampo = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
    setErrores((prev) => ({ ...prev, [campo]: null }));
    setMensaje(null);
  };

  const validarFormulario = () => {
    const next = {};

    const resultadoRut = validarRutChileno(form.rut);
    if (!resultadoRut.valido) {
      next.rut = resultadoRut.mensaje;
    }

    if (!String(form.razonSocial || "").trim()) {
      next.razonSocial = "La razón social es obligatoria.";
    }

    if (!String(form.direccion || "").trim()) {
      next.direccion = "La dirección es obligatoria.";
    }

    if (!String(form.comuna || "").trim()) {
      next.comuna = "La comuna es obligatoria.";
    }

    if (!String(form.telefono || "").trim()) {
      next.telefono = "El teléfono es obligatorio.";
    }

    if (
      modoDialog === "editar" &&
      (form.idCobranzas === null || form.idCobranzas === undefined)
    ) {
      next.idCobranzas = "El id de cobranzas es obligatorio.";
    }

    setErrores(next);
    return Object.keys(next).length === 0;
  };

  const guardarCliente = async () => {
    if (!validarFormulario()) return;

    setIsLoading(true);
    try {
      const rutValidado = validarRutChileno(form.rut);
      const rutFormateado = rutValidado.rutFormateado || formatearRut(form.rut);
      const razonSocial = String(form.razonSocial || "").trim();
      const direccion = String(form.direccion || "").trim();
      const comuna = String(form.comuna || "").trim();
      const telefono = String(form.telefono || "").trim();

      let idCobranzasFinal = form.idCobranzas;

      if (modoDialog === "crear") {
        idCobranzasFinal = await crearClienteEnCobranzas({
          id: form.id,
          rut: rutFormateado,
          razonSocial,
        });

        const payload = {
          rut: rutFormateado,
          razonSocial,
          direccion,
          comuna,
          telefono,
          idCobranzas: idCobranzasFinal,
        };

        await fetchJson(joinUrl(baseUrl, "cliente/insertarCliente"), {
          method: "POST",
          body: JSON.stringify(payload),
        });

        setMensaje({
          severity: "success",
          text: "Cliente creado correctamente.",
        });
      } else {
        const payload = {
          rut: rutFormateado,
          razonSocial,
          direccion,
          comuna,
          telefono,
          idCobranzas: idCobranzasFinal,
        };

        await fetchJson(
          joinUrl(
            baseUrl,
            `cliente/actualizarCliente?id=${encodeURIComponent(form.id)}`,
          ),
          {
            method: "PUT",
            body: JSON.stringify(payload),
          },
        );

        setMensaje({
          severity: "success",
          text: "Cliente actualizado correctamente.",
        });
      }

      await cargarClientes();
      cerrarDialog();
    } catch (e) {
      console.error(e);
      setMensaje({
        severity: "error",
        text: e?.message || "No se pudo guardar el cliente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const accionesBody = (row) => {
    return (
      <div className="flex gap-2">
        <Button
          label="Editar"
          icon="pi pi-pencil"
          className="p-button-sm"
          onClick={() => abrirEditar(row)}
        />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column gap-3 md:flex-row md:justify-content-between md:align-items-center">
      <div>
        <h2 className="m-0">Clientes</h2>
        <small>Crear, editar y consultar clientes.</small>
      </div>

      <div className="flex flex-column md:flex-row gap-2">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por RUT, razón social, comuna..."
          />
        </span>

        <Button
          label="Recargar"
          icon="pi pi-refresh"
          className="p-button-secondary"
          onClick={cargarClientes}
        />

        <Button label="Nuevo cliente" icon="pi pi-plus" onClick={abrirCrear} />
      </div>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          {isLoading && (
            <div className="flex justify-content-center my-4">
              <ProgressSpinner />
            </div>
          )}

          {!isLoading && (
            <DataTable
              value={clientesFiltrados}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              responsiveLayout="scroll"
              dataKey="id"
              header={header}
              emptyMessage="No se encontraron clientes."
            >
              <Column field="id" header="ID" style={{ width: "6rem" }} />
              <Column field="rut" header="RUT" />
              <Column field="razonSocial" header="Razón Social" />
              <Column field="direccion" header="Dirección" />
              <Column field="comuna" header="Comuna" />
              <Column field="telefono" header="Teléfono" />
              <Column field="idCobranzas" header="ID Cobranzas" />
              <Column
                body={accionesBody}
                header="Acciones"
                style={{ width: "10rem" }}
              />
            </DataTable>
          )}
        </div>
      </div>

      <Dialog
        header={modoDialog === "crear" ? "Nuevo Cliente" : "Editar Cliente"}
        visible={dialogVisible}
        style={{ width: "55rem", maxWidth: "95vw" }}
        modal
        onHide={cerrarDialog}
      >
        <div className="grid">
          <div className="col-12">
            {mensaje && (
              <Message severity={mensaje.severity} text={mensaje.text} />
            )}
          </div>

          <div className="col-12 md:col-6">
            <label className="block mb-2">RUT</label>
            <InputText
              className="w-full"
              value={form.rut}
              onChange={(e) => {
                const valor = e.target.value.replace(/[^0-9kK.-]/g, "");
                setCampo("rut", valor);
              }}
              onBlur={() => {
                const limpio = limpiarRut(form.rut);
                if (!limpio) return;
                setCampo("rut", formatearRut(limpio));
              }}
              placeholder="12.345.678-5"
            />
            {errores.rut && (
              <small className="p-error block mt-1">{errores.rut}</small>
            )}
          </div>

          <div className="col-12 md:col-6">
            <label className="block mb-2">Razón Social</label>
            <InputText
              className="w-full"
              value={form.razonSocial}
              onChange={(e) => setCampo("razonSocial", e.target.value)}
            />
            {errores.razonSocial && (
              <small className="p-error block mt-1">
                {errores.razonSocial}
              </small>
            )}
          </div>

          <div className="col-12">
            <label className="block mb-2">Dirección</label>
            <InputText
              className="w-full"
              value={form.direccion}
              onChange={(e) => setCampo("direccion", e.target.value)}
            />
            {errores.direccion && (
              <small className="p-error block mt-1">{errores.direccion}</small>
            )}
          </div>

          <div className="col-12 md:col-4">
            <label className="block mb-2">Comuna</label>
            <InputText
              className="w-full"
              value={form.comuna}
              onChange={(e) => setCampo("comuna", e.target.value)}
            />
            {errores.comuna && (
              <small className="p-error block mt-1">{errores.comuna}</small>
            )}
          </div>

          <div className="col-12 md:col-4">
            <label className="block mb-2">Teléfono</label>
            <InputText
              className="w-full"
              value={form.telefono}
              onChange={(e) => setCampo("telefono", e.target.value)}
            />
            {errores.telefono && (
              <small className="p-error block mt-1">{errores.telefono}</small>
            )}
          </div>

          <div className="col-12 md:col-4">
            <label className="block mb-2">ID Cobranzas</label>
            <InputNumber
              className="w-full"
              value={form.idCobranzas}
              onValueChange={(e) => setCampo("idCobranzas", e.value)}
              useGrouping={false}
              disabled={modoDialog === "crear"}
              placeholder={
                modoDialog === "crear" ? "Se genera automáticamente" : ""
              }
            />
            {modoDialog === "crear" && (
              <small className="block mt-1 text-500">
                Se obtiene automáticamente desde Cobranzas al crear el cliente.
              </small>
            )}
            {errores.idCobranzas && (
              <small className="p-error block mt-1">
                {errores.idCobranzas}
              </small>
            )}
          </div>

          <div className="col-12 flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={cerrarDialog}
            />
            <Button
              label={modoDialog === "crear" ? "Crear" : "Guardar"}
              icon="pi pi-check"
              onClick={guardarCliente}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Clientes;
